import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Calculator } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];

export default function TaxCodes() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const [taxCodes, setTaxCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/tax-codes");
      setTaxCodes(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (taxCode) => {
    try {
      await api.patch(`/masters/tax-codes/${taxCode.id}`, { is_active: !taxCode.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="tax-codes-page">
      <PageHeader
        title="Tax Codes"
        description="GST rates used across procurement and billing, with intra/inter-state CGST/SGST/IGST split."
        action={canManage && <CreateTaxCodeDialog onCreated={load} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && taxCodes.length === 0 ? (
          <EmptyState title="No tax codes yet" testId="tax-codes-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Rate</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {taxCodes.map((t) => (
                <TableRow key={t.id} data-testid={`tax-code-row-${t.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{t.code}</TableCell>
                  <TableCell className="text-foreground">{t.name}</TableCell>
                  <TableCell className="font-mono text-xs text-foreground">{t.rate}%</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={t.is_active} disabled={!canManage} onCheckedChange={() => toggleActive(t)} data-testid={`tax-code-active-toggle-${t.code}`} />
                      <Badge variant={t.is_active ? "default" : "secondary"}>{t.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {taxCodes.length > 0 && <GstSplitCalculator taxCodes={taxCodes} />}
    </div>
  );
}

function GstSplitCalculator({ taxCodes }) {
  const [taxCodeId, setTaxCodeId] = useState(taxCodes[0]?.id || "");
  const [sellerState, setSellerState] = useState("Rajasthan");
  const [buyerState, setBuyerState] = useState("Rajasthan");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/masters/tax-codes/${taxCodeId}/gst-split`, {
        seller_state: sellerState || null, buyer_state: buyerState || null,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="gst-split-calculator">
      <div className="mb-3 flex items-center gap-2 text-muted-foreground">
        <Calculator className="h-4 w-4" />
        <h2 className="text-sm font-semibold uppercase tracking-wider">GST split checker</h2>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div>
          <Label>Tax code</Label>
          <Select value={taxCodeId} onValueChange={setTaxCodeId}>
            <SelectTrigger data-testid="gst-tax-code-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{taxCodes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Seller state</Label>
          <Input data-testid="gst-seller-state-input" value={sellerState} onChange={(e) => setSellerState(e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label>Buyer state</Label>
          <Input data-testid="gst-buyer-state-input" value={buyerState} onChange={(e) => setBuyerState(e.target.value)} className="mt-1.5" />
        </div>
        <div className="flex items-end">
          <Button type="button" disabled={!taxCodeId || loading} onClick={calculate} data-testid="gst-split-calculate-button" className="w-full">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Calculate
          </Button>
        </div>
      </div>
      {result && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="gst-split-result">
          <ResultStat label="CGST" value={`${result.cgst_rate}%`} />
          <ResultStat label="SGST" value={`${result.sgst_rate}%`} />
          <ResultStat label="IGST" value={`${result.igst_rate}%`} />
          <ResultStat label="Type" value={result.is_inter_state ? "Inter-state" : "Intra-state"} />
        </div>
      )}
    </div>
  );
}

function ResultStat({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3 text-center">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-mono text-lg font-bold text-foreground">{value}</p>
    </div>
  );
}

function CreateTaxCodeDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", rate: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/masters/tax-codes", { ...form, rate: Number(form.rate) });
      toast.success(`Tax code ${form.code} created`);
      setForm({ code: "", name: "", rate: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-tax-code-button"><Plus className="h-4 w-4" /> New Tax Code</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Tax Code</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-tax-code-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input required data-testid="tax-code-code-input" value={form.code} onChange={set("code")} className="mt-1.5" placeholder="GST18" />
              <FieldError error={errors.code} />
            </div>
            <div>
              <Label>Rate (%)</Label>
              <Input required type="number" step="0.01" data-testid="tax-code-rate-input" value={form.rate} onChange={set("rate")} className="mt-1.5" placeholder="18" />
              <FieldError error={errors.rate} />
            </div>
          </div>
          <div>
            <Label>Name</Label>
            <Input required data-testid="tax-code-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="GST 18%" />
            <FieldError error={errors.name} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-tax-code-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
